import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Trash2,
  Pencil,
  Plus,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Loader2,
  AlertCircle,
  Sparkles,
  UploadCloud,
} from 'lucide-react';
import {
  Card,
  Badge,
  Button,
  Input,
  Select,
  ConfirmationModal,
  toast,
  MoneyInput,
} from '../../components/v2/V2Components';
import UtilityService from '../../services/UtilityService';
import BulkBillImport from './BulkBillImport';
import ConfigService, { DEFAULT_USAGE_UNITS } from '../../services/ConfigService';
import {
  UTILITY_TYPES,
  getUtilityMeta,
  getUtilityDisplayName,
} from '../../utils/utilityIcons';
import { isSafeHttpUrl } from '../../utils/validation';
import { sortBillsNewestFirst } from '../../utils/billCoverage';
import BillCoverageStrip from '../../components/v2/BillCoverageStrip';
import {
  isIsoDate,
  parseLocalDate,
  formatDate,
  formatDateRange,
  toDateInputValue,
  formatCurrency,
  formatUsage,
  isImageFile,
  routeExtractedUnit,
} from '../../utils/billFormat';
import usePageView from '../../hooks/usePageView';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';

// Hides the native number-input spinner (up/down steppers) in WebKit + Firefox.
// Mirrors the NO_SPINNER idiom in MortgageStatementForm.jsx / MortgageForm.jsx.
const NO_SPINNER =
  '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

// Sentinel option value that flips the unit dropdown into free-text entry so a
// user whose provider bills in a unit the server list doesn't cover can add
// their own.
const CUSTOM_UNIT = '__custom__';

const BillRow = ({ bill, onEdit, onDelete, hideActions = false }) => {
  const filename = bill.original_filename || bill.filename || '';
  const isImg = bill.url && isImageFile(filename);
  return (
    <tr className="border-b border-[var(--border)] last:border-0">
      <td className="py-3 px-2 text-sm text-[var(--text-main)]">
        <div>{formatDateRange(bill.period_start, bill.period_end)}</div>
        {bill.statement_date && (
          <div className="text-xs text-[var(--text-muted)]">
            Statement {formatDate(bill.statement_date)}
          </div>
        )}
      </td>
      <td className="py-3 px-2 text-sm font-semibold text-[var(--text-main)]">
        {formatCurrency(bill.amount)}
      </td>
      <td className="py-3 px-2 text-sm text-[var(--text-secondary)] whitespace-nowrap">
        {formatUsage(bill.usage_quantity, bill.usage_unit)}
      </td>
      <td className="py-3 px-2 text-sm">
        {bill.url ? (
          <a
            href={bill.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[var(--primary)] hover:underline"
          >
            {isImg ? <ImageIcon size={14} /> : <FileText size={14} />}
            <span className="truncate max-w-[140px]">
              {filename || 'View file'}
            </span>
          </a>
        ) : (
          <span className="text-[var(--text-muted)]">—</span>
        )}
      </td>
      <td className="py-3 px-2 text-sm text-[var(--text-secondary)] max-w-[160px] truncate">
        {bill.notes || ''}
      </td>
      <td className="py-3 px-2 text-right whitespace-nowrap">
        {/* While the bill form is open, ALL competing affordances are hidden
            (ElectricalTab's suppress rule) — no editing a second bill or
            deleting the one being edited mid-form. */}
        {!hideActions && (
          <>
            <button
              onClick={() => onEdit(bill)}
              className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-hover)]"
              aria-label="Edit bill"
              title="Edit bill"
            >
              <Pencil size={16} />
            </button>
            <button
              onClick={() => onDelete(bill)}
              className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--error)] hover:bg-[var(--bg-hover)]"
              aria-label="Delete bill"
              title="Delete bill"
            >
              <Trash2 size={16} />
            </button>
          </>
        )}
      </td>
    </tr>
  );
};

// Create AND edit form for a bill. `bill` null/absent = create; a bill object
// = edit (PanelForm's isEdit convention). The parent renders it with
// key={editingBill?.id ?? 'new'} so switching create<->edit or bill<->bill
// remounts and the lazy initializers below re-seed — no hydration effect.
const InlineBillForm = ({ utilityId, utilityType, bill = null, onSaved, onCancel }) => {
  const isEdit = !!(bill && bill.id);
  const [periodStart, setPeriodStart] = useState(() => toDateInputValue(bill?.period_start));
  const [periodEnd, setPeriodEnd] = useState(() => toDateInputValue(bill?.period_end));
  const [statementDate, setStatementDate] = useState(() => toDateInputValue(bill?.statement_date));
  const [amount, setAmount] = useState(() => (bill?.amount != null ? String(bill.amount) : ''));
  const [usageQuantity, setUsageQuantity] = useState(() =>
    bill?.usage_quantity != null ? String(bill.usage_quantity) : '',
  );
  // Units offered for this utility type come from /api/v1/config/usage-units so
  // the vocabulary is maintained in one place server-side. Seed from the local
  // mirror (DEFAULT_USAGE_UNITS) so the first render already shows the right
  // options before the fetch resolves; the fetched copy overrides on load.
  const [unitOptions, setUnitOptions] = useState(
    () => DEFAULT_USAGE_UNITS[utilityType] || [],
  );
  // Pre-fill the unit with this type's default (first in its list), but keep it
  // editable: the picker is a dropdown of the type's units plus an "Add
  // custom…" escape hatch for providers that bill in something unlisted.
  // When editing, the bill's stored unit wins.
  const [usageUnit, setUsageUnit] = useState(
    () => bill?.usage_unit || (DEFAULT_USAGE_UNITS[utilityType] || [])[0] || '',
  );
  // When true the unit field is a free-text input rather than the dropdown.
  // Starts true for types with no standard metered unit (empty list), or when
  // editing a bill whose stored unit isn't in the type's list. The
  // ConfigService effect below deliberately never forces this back off.
  const [customUnit, setCustomUnit] = useState(() => {
    if (bill?.usage_unit) return !(DEFAULT_USAGE_UNITS[utilityType] || []).includes(bill.usage_unit);
    return (DEFAULT_USAGE_UNITS[utilityType] || []).length === 0;
  });
  const [notes, setNotes] = useState(() => bill?.notes || '');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [extracting, setExtracting] = useState(false);

  // Re-seed the unit state synchronously (during render) if utilityType changes
  // under a mounted form. Today the form unmounts between opens so this rarely
  // fires, but the render-phase adjustment is the React-recommended way to keep
  // derived state in sync with a prop — and it closes the edge case where
  // switching from a no-standard-unit type (cable) to one with units (electric)
  // would otherwise leave customUnit stuck true. Ref: react.dev "You Might Not
  // Need an Effect" → adjusting state when a prop changes.
  const [prevUtilityType, setPrevUtilityType] = useState(utilityType);
  if (utilityType !== prevUtilityType) {
    setPrevUtilityType(utilityType);
    const seeded = DEFAULT_USAGE_UNITS[utilityType] || [];
    setUnitOptions(seeded);
    setUsageUnit(seeded[0] || '');
    setCustomUnit(seeded.length === 0);
  }

  // Fetch the authoritative per-type unit list. On resolve, adopt this type's
  // options; if the user hasn't diverged from the seeded default yet, re-point
  // the selection at the fetched default too (covers the rare case where the
  // server list differs from the local mirror). Keyed on utilityType so a
  // prop change re-fetches.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const map = await ConfigService.getUsageUnits();
      if (cancelled) return;
      const options = map[utilityType] || [];
      setUnitOptions(options);
      const seededDefault = (DEFAULT_USAGE_UNITS[utilityType] || [])[0] || '';
      // Only re-seed the selection if the user is still on the untouched
      // default and we're not already in custom-entry mode.
      setUsageUnit((current) =>
        current === seededDefault ? options[0] || '' : current,
      );
      // Empty option list → free-text only; a non-empty list must NOT force
      // the dropdown back on if the user already picked "Add custom…".
      setCustomUnit((current) => (options.length === 0 ? true : current));
    })();
    return () => {
      cancelled = true;
    };
  }, [utilityType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!periodStart || !periodEnd || !amount) {
      toast.error('Period and amount are required');
      return;
    }
    const startDate = parseLocalDate(periodStart);
    const endDate = parseLocalDate(periodEnd);
    if (startDate && endDate && endDate < startDate) {
      toast.error('Period end must be on or after period start');
      return;
    }
    // Number.isFinite rejects NaN, Infinity, -Infinity, and non-numbers in
    // one shot — strictly stronger than `!isNaN()`. parseFloat('1e309') is
    // Infinity in JS, which would otherwise sail through and 400 the API.
    const numericAmount = parseFloat(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }
    let numericUsage;
    if (usageQuantity !== '') {
      numericUsage = parseFloat(usageQuantity);
      if (!Number.isFinite(numericUsage) || numericUsage <= 0) {
        toast.error('Usage must be greater than 0');
        return;
      }
    }

    try {
      setSubmitting(true);
      if (isEdit) {
        // PUT is JSON-only. Nullable fields MUST be present with an explicit
        // null when blank — the backend's `"field" in data` convention treats
        // an omitted key as "leave unchanged", which would make cleared fields
        // silently un-clearable.
        let saved = await UtilityService.updateBill(bill.id, {
          period_start: periodStart,
          period_end: periodEnd,
          amount: numericAmount,
          statement_date: statementDate || null,
          usage_quantity: numericUsage ?? null,
          usage_unit: numericUsage != null ? (usageUnit || null) : null,
          notes: notes || null,
        });
        if (file) {
          try {
            // The attach response carries the fresh presigned url + filename,
            // so it supersedes the PUT response as the saved bill.
            saved = await UtilityService.attachBillFile(bill.id, file);
          } catch (fileErr) {
            toast.error(
              fileErr.message
                ? `Bill saved, but the file upload failed: ${fileErr.message}`
                : 'Bill saved, but the file upload failed — edit the bill to try again',
            );
            onSaved(saved);
            return;
          }
        }
        toast.success('Bill updated');
        onSaved(saved);
      } else {
        const created = await UtilityService.createBill(utilityId, {
          period_start: periodStart,
          period_end: periodEnd,
          statement_date: statementDate || undefined,
          amount: numericAmount,
          usage_quantity: numericUsage,
          usage_unit: numericUsage != null ? (usageUnit || undefined) : undefined,
          notes: notes || undefined,
          file: file || undefined,
        });
        toast.success('Bill added');
        onSaved(created);
      }
    } catch (err) {
      toast.error(err.message || (isEdit ? 'Failed to update bill' : 'Failed to add bill'));
    } finally {
      setSubmitting(false);
    }
  };

  // Routing lives in utils/billFormat so the bulk-import fix card applies the
  // identical rule; this wrapper just pushes the result into form state.
  const applyExtractedUnit = (raw) => {
    const routed = routeExtractedUnit(raw, unitOptions);
    if (!routed) return;
    setCustomUnit(routed.custom);
    setUsageUnit(routed.unit);
  };

  const handleAutofill = async () => {
    if (!file) return;
    try {
      setExtracting(true);
      const { bill: extracted } = await UtilityService.extractBill(utilityId, file);
      const hasValue = (v) => v !== null && v !== undefined && v !== '';
      if (!extracted || !Object.values(extracted).some(hasValue)) {
        toast.error('No fields could be read from that file');
        return;
      }
      // Overwrite-merge (MortgageStatementForm's semantics): any field the
      // scan returned replaces the current value; the user reviews before
      // saving. Dates are gated on strict ISO so a malformed model date can't
      // wedge a <input type="date"> into an unclearable state.
      const isoOk = isIsoDate;
      if (isoOk(extracted.period_start)) setPeriodStart(extracted.period_start);
      if (isoOk(extracted.period_end)) setPeriodEnd(extracted.period_end);
      if (isoOk(extracted.statement_date)) setStatementDate(extracted.statement_date);
      if (hasValue(extracted.amount)) setAmount(String(extracted.amount));
      if (hasValue(extracted.usage_quantity)) setUsageQuantity(String(extracted.usage_quantity));
      if (hasValue(extracted.usage_unit)) applyExtractedUnit(extracted.usage_unit);
      toast.success('Bill read — review the fields before saving');
    } catch (err) {
      toast.error(err.message || 'Could not read that bill file');
    } finally {
      setExtracting(false);
    }
  };

  return (
    <Card className="p-5 mb-4 border border-[var(--border)]">
      <div className="flex justify-between items-center mb-3">
        <h4 className="text-sm font-bold text-[var(--text-main)] uppercase tracking-wide">
          {isEdit ? 'Edit Bill' : 'New Bill'}
        </h4>
      </div>
      {/* aria-label makes the form queryable via screen.getByRole('form', { name: ... })
          so tests can scope DOM queries to this form instead of the whole document. */}
      <form
        onSubmit={handleSubmit}
        className="space-y-3"
        aria-label={isEdit ? 'Edit Bill' : 'Add Bill'}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1">
              Period Start *
            </label>
            <Input
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1">
              Period End *
            </label>
            <Input
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
              required
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1">
              Statement Date
            </label>
            <Input
              type="date"
              value={statementDate}
              onChange={(e) => setStatementDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1">
              Amount (USD) *
            </label>
            <MoneyInput
              name="amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1">
              Usage
            </label>
            {/* Value takes 2/3 of the row, unit picker the remaining 1/3.
                items-start so a custom-unit text input dropping in below the
                <select> doesn't stretch the value field. */}
            <div className="flex gap-2 items-start">
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step="any"
                value={usageQuantity}
                onChange={(e) => setUsageQuantity(e.target.value)}
                placeholder="e.g. 850"
                // The visible "Usage" <label> isn't tied via htmlFor — give the
                // input an explicit name so tests (and screen readers) can
                // address it: screen.getByLabelText('Usage quantity').
                aria-label="Usage quantity"
                // Larger, easier-to-read value; NO_SPINNER strips the native
                // up/down steppers so the number reads cleanly.
                className={`basis-2/3 text-lg ${NO_SPINNER}`}
              />
              <div className="basis-1/3">
                {customUnit ? (
                  <>
                    <Input
                      type="text"
                      value={usageUnit}
                      onChange={(e) => setUsageUnit(e.target.value)}
                      placeholder="unit"
                      aria-label="Usage unit"
                      // Mirrors the schema's max_length=16 (schemas/utility.py);
                      // the cap here prevents a confusing 400 round-trip on
                      // overlong input.
                      maxLength={16}
                      autoFocus
                    />
                    {/* Only offer "back to list" when the type actually has
                        standard units — for cable/trash/other the list is empty
                        and free text is the only sensible mode. */}
                    {unitOptions.length > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          setCustomUnit(false);
                          setUsageUnit(unitOptions[0] || '');
                        }}
                        className="mt-1 text-xs text-[var(--primary)] hover:underline"
                      >
                        Use a standard unit
                      </button>
                    )}
                  </>
                ) : (
                  <Select
                    value={usageUnit}
                    aria-label="Usage unit"
                    onChange={(e) => {
                      if (e.target.value === CUSTOM_UNIT) {
                        // Switch to free text; clear so the field starts empty
                        // for the user to type their provider's unit.
                        setUsageUnit('');
                        setCustomUnit(true);
                      } else {
                        setUsageUnit(e.target.value);
                      }
                    }}
                  >
                    <option value="">unit</option>
                    {unitOptions.map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                    <option value={CUSTOM_UNIT}>Add custom…</option>
                  </Select>
                )}
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1">
              File (PDF or image, max 10MB)
            </label>
            {/* Visible native browse button — stays as a raw <input> rather than
                going through useFilePicker (which is for hidden-input-behind-
                custom-button flows). Use bare image/* + application/pdf (no
                extension hints) so iOS still shows the chooser sheet; see
                hooks/useFilePicker.jsx for the iOS rationale. */}
            <input
              type="file"
              accept="image/*,application/pdf"
              aria-label="Bill file"
              disabled={extracting}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-[var(--text-main)] disabled:opacity-60"
              data-testid="file-picker-input"
            />
            {isEdit && (bill.original_filename || bill.filename) && (
              <p className="mt-1 text-xs text-[var(--text-muted)]">
                Current file: {bill.original_filename || bill.filename}. Choosing
                a new file replaces it.
              </p>
            )}
            {!isEdit && file && (
              <>
                <button
                  type="button"
                  onClick={handleAutofill}
                  disabled={extracting}
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 border border-[var(--primary)] text-[var(--primary)] rounded-lg hover:bg-[var(--bg-hover)] transition-colors text-sm font-medium disabled:opacity-60"
                >
                  {extracting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                  <span>{extracting ? 'Reading bill…' : 'Upload & autofill from this file'}</span>
                </button>
                <p className="mt-2 text-xs text-[var(--text-muted)]">
                  Autofill reads the bill and pre-fills the fields above for you
                  to review. It counts as one scan. The file stays attached when
                  you save.
                </p>
              </>
            )}
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1">
            Notes
          </label>
          <Input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional"
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            {isEdit ? 'Save' : 'Add Bill'}
          </Button>
        </div>
      </form>
    </Card>
  );
};

const UtilityDetailsPage = () => {
  usePageView(ANALYTICS_EVENTS.UTILITIES_DETAILS_VIEWED);
  const { houseId, utilityId } = useParams();
  const navigate = useNavigate();

  const [utility, setUtility] = useState(null);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Account form state
  const [type, setType] = useState('electric');
  const [name, setName] = useState('');
  const [accountUrl, setAccountUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Bill flow: one panel slot, never two. An enum rather than two booleans so
  // "Add Bill and Bulk Bills open at once" is unrepresentable rather than
  // merely avoided. editingBill null = create, a bill object = edit
  // (ElectricalTab's editingPanel convention).
  const [billPanel, setBillPanel] = useState(null); // null | 'form' | 'import'
  const [editingBill, setEditingBill] = useState(null);

  // Confirmations
  const [confirmDeleteUtility, setConfirmDeleteUtility] = useState(false);
  const [confirmDeleteBill, setConfirmDeleteBill] = useState(null);
  const [deletingBill, setDeletingBill] = useState(false);
  const [deletingUtility, setDeletingUtility] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [u, billsData] = await Promise.all([
          UtilityService.getUtility(utilityId),
          UtilityService.getBills(utilityId),
        ]);
        if (cancelled) return;
        // Guard against URL tampering: if the URL says this utility lives
        // under a different house, kick the user back to the right house.
        if (houseId && String(u.house_id) !== String(houseId)) {
          navigate(`/houses/${u.house_id}/utilities/${u.id}`, { replace: true });
          return;
        }
        setUtility(u);
        setType(u.type);
        setName(u.name || '');
        setAccountUrl(u.account_url || '');
        setNotes(u.notes || '');
        setBills(billsData.bills || []);
        setLoadError(null);
      } catch (err) {
        if (cancelled) return;
        if (err.code === 404) setLoadError('Utility not found.');
        else if (err.code === 401) setLoadError('Authentication required.');
        else setLoadError(err.message || 'Failed to load utility.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [utilityId, houseId, navigate]);

  // Tracks whether the component is still mounted so refreshAggregates
  // (called from event handlers, not a useEffect) doesn't setState after
  // unmount. The initial-load useEffect uses its own `cancelled` flag.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refreshAggregates = async () => {
    try {
      const fresh = await UtilityService.getUtility(utilityId);
      if (mountedRef.current) setUtility(fresh);
    } catch (err) {
      if (!mountedRef.current) return;
      // Non-fatal: stale avg/count are tolerable, but we want a signal in
      // dev tools rather than a silent swallow.
      // eslint-disable-next-line no-console
      console.warn('Failed to refresh utility aggregates', err);
    }
  };

  const goBackToUtilities = () => {
    navigate(`/houses/${houseId}?tab=utilities`);
  };

  const handleSaveMeta = async (e) => {
    e.preventDefault();
    if (type === 'other' && !name.trim()) {
      toast.error('Name is required when type is "other"');
      return;
    }
    // Use the same isSafeHttpUrl helper the render guard uses (UtilityCard +
    // the "Open account" link below). Keeping the read- and write-side checks
    // identical prevents users from saving a value that immediately stops
    // rendering — e.g. `https://[invalid]` matches the old `^https?://` regex
    // but fails URL parsing, so the link would silently disappear after save.
    if (accountUrl && !isSafeHttpUrl(accountUrl)) {
      toast.error('Account URL must be a valid http:// or https:// link');
      return;
    }
    try {
      setSaving(true);
      const updated = await UtilityService.updateUtility(utilityId, {
        type,
        name: name.trim() || null,
        account_url: accountUrl || null,
        notes: notes || null,
      });
      setUtility(updated);
      toast.success('Utility updated');
    } catch (err) {
      toast.error(err.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleBillSaved = (savedBill) => {
    // Re-sort on both paths to match the server's period_end-desc order: a period
    // edit can move the row, and a new bill may be older than the ones shown.
    setBills((prev) =>
      sortBillsNewestFirst(
        prev.some((b) => b.id === savedBill.id)
          ? prev.map((b) => (b.id === savedBill.id ? savedBill : b))
          : [savedBill, ...prev],
      ),
    );
    setBillPanel(null);
    setEditingBill(null);
    refreshAggregates();
  };

  const closeBillForm = () => {
    setBillPanel(null);
    setEditingBill(null);
  };

  // Bulk import merges rows as each file lands rather than at the end, so the
  // table and coverage strip fill in live and a mid-import crash leaves the UI
  // matching the database. Dedupes on id because a fixed needs-attention row
  // arrives through this same path.
  const handleBulkBillsCreated = (newBills) => {
    if (!newBills?.length) return;
    setBills((prev) => {
      const known = new Set(prev.map((b) => b.id));
      const fresh = newBills.filter((b) => b && !known.has(b.id));
      if (!fresh.length) return prev;
      return sortBillsNewestFirst([...fresh, ...prev]);
    });
  };

  const closeBulkImport = () => {
    setBillPanel(null);
    // One refresh at the end: the trailing average is a full GET and only
    // matters once the import is over.
    refreshAggregates();
  };

  const handleDeleteBill = async () => {
    if (!confirmDeleteBill) return;
    try {
      setDeletingBill(true);
      await UtilityService.deleteBill(confirmDeleteBill.id);
      setBills((prev) => prev.filter((b) => b.id !== confirmDeleteBill.id));
      setConfirmDeleteBill(null);
      toast.success('Bill deleted');
      refreshAggregates();
    } catch (err) {
      toast.error(err.message || 'Failed to delete bill');
    } finally {
      setDeletingBill(false);
    }
  };

  const handleDeleteUtility = async () => {
    try {
      setDeletingUtility(true);
      await UtilityService.deleteUtility(utilityId);
      toast.success('Utility deleted');
      goBackToUtilities();
    } catch (err) {
      toast.error(err.message || 'Failed to delete utility');
      setDeletingUtility(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Card className="p-10 flex items-center justify-center text-[var(--text-muted)]">
          <Loader2 className="animate-spin mr-2" size={18} />
          Loading utility…
        </Card>
      </div>
    );
  }

  if (loadError || !utility) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Button leadingIcon={ArrowLeft} variant="ghost" onClick={goBackToUtilities}>
          Back
        </Button>
        <Card className="p-10 text-center mt-4">
          <AlertCircle size={32} className="mx-auto mb-3 text-[var(--error)]" />
          <p className="text-[var(--text-main)]">{loadError || 'Utility not found.'}</p>
        </Card>
      </div>
    );
  }

  const meta = getUtilityMeta(utility.type);
  const Icon = meta.icon;
  const displayName = getUtilityDisplayName(utility);

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <button
        onClick={goBackToUtilities}
        className="inline-flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-[var(--text-main)] mb-4"
      >
        <ArrowLeft size={16} /> Back to utilities
      </button>

      <div className="flex items-center gap-3 mb-6">
        <div className={`p-3 rounded-xl bg-[var(--bg-hover)] ${meta.color}`}>
          <Icon size={28} />
        </div>
        <div>
          <h1 className="font-headline text-2xl font-bold text-[var(--text-main)]">
            {displayName}
          </h1>
          <div className="text-sm text-[var(--text-secondary)] capitalize">
            {meta.label}
          </div>
        </div>
      </div>

      <Card className="p-5 mb-6">
        <h2 className="text-sm font-bold text-[var(--text-main)] mb-4 uppercase tracking-wide">
          Account
        </h2>
        <form onSubmit={handleSaveMeta} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1">
                Type
              </label>
              <Select value={type} onChange={(e) => setType(e.target.value)}>
                {UTILITY_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {getUtilityMeta(t).label}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1">
                Name {type === 'other' && <span className="text-red-500">*</span>}
              </label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={type === 'other' ? 'Required' : 'e.g. ConEd'}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1">
              Account URL
            </label>
            <Input
              type="url"
              value={accountUrl}
              onChange={(e) => setAccountUrl(e.target.value)}
              placeholder="https://provider.example.com/account"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1">
              Notes
            </label>
            <Input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div className="flex flex-col-reverse md:flex-row md:justify-between md:items-center gap-3 pt-2">
            <Button
              type="button"
              variant="destructive"
              onClick={() => setConfirmDeleteUtility(true)}
            >
              Delete Utility
            </Button>
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              {isSafeHttpUrl(utility.account_url) && (
                <a
                  href={isSafeHttpUrl(utility.account_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-[var(--primary)] hover:underline"
                >
                  <ExternalLink size={14} /> Open account
                </a>
              )}
              <Button type="submit" loading={saving}>
                Save
              </Button>
            </div>
          </div>
        </form>
      </Card>

      <Card className="p-5">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4">
          <div>
            <h2 className="text-sm font-bold text-[var(--text-main)] uppercase tracking-wide">
              Bills
            </h2>
            <div className="text-xs text-[var(--text-secondary)] mt-1 flex items-center gap-2 flex-wrap">
              <span>
                Trailing 12-month average:{' '}
                <span className="font-semibold text-[var(--primary)]">
                  {formatCurrency(utility.monthly_cost_average)}
                </span>
              </span>
              <Badge
                status={`${bills.length} ${bills.length === 1 ? 'bill' : 'bills'}`}
              />
            </div>
          </div>
          {billPanel === null && (
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <Button
                leadingIcon={Plus}
                className="flex-1 min-w-0 md:flex-none"
                onClick={() => setBillPanel('form')}
              >
                Add Bill
              </Button>
              <Button
                variant="secondary"
                leadingIcon={UploadCloud}
                className="flex-1 min-w-0 md:flex-none"
                onClick={() => setBillPanel('import')}
              >
                Bulk Bills
              </Button>
            </div>
          )}
        </div>

        {billPanel === 'import' && (
          <BulkBillImport
            utilityId={utility.id}
            utilityType={utility.type}
            existingBills={bills}
            onBillsCreated={handleBulkBillsCreated}
            onClose={closeBulkImport}
          />
        )}

        {billPanel === 'form' && (
          <InlineBillForm
            // Remount on create<->edit and bill<->bill switches so the lazy
            // state initializers re-seed from the right bill.
            key={editingBill?.id ?? 'new'}
            utilityId={utility.id}
            utilityType={utility.type}
            bill={editingBill}
            onSaved={handleBillSaved}
            onCancel={closeBillForm}
          />
        )}

        {bills.length > 0 && (
          <div className="mb-5">
            <BillCoverageStrip key={utility.id} bills={bills} />
          </div>
        )}

        {bills.length === 0 ? (
          <div className="text-center py-8 text-[var(--text-muted)]">
            No bills yet. Add one to start tracking your monthly average.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs uppercase text-[var(--text-secondary)] border-b border-[var(--border)]">
                  <th className="py-2 px-2 font-semibold">Period</th>
                  <th className="py-2 px-2 font-semibold">Amount</th>
                  <th className="py-2 px-2 font-semibold">Usage</th>
                  <th className="py-2 px-2 font-semibold">File</th>
                  <th className="py-2 px-2 font-semibold">Notes</th>
                  <th className="py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {bills.map((b) => (
                  <BillRow
                    key={b.id}
                    bill={b}
                    hideActions={billPanel !== null}
                    onEdit={(bill) => {
                      setEditingBill(bill);
                      setBillPanel('form');
                    }}
                    onDelete={(bill) => setConfirmDeleteBill(bill)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ConfirmationModal
        isOpen={confirmDeleteUtility}
        onClose={() => setConfirmDeleteUtility(false)}
        onConfirm={handleDeleteUtility}
        title="Delete Utility?"
        message={`This will permanently delete the utility and all ${bills.length} ${bills.length === 1 ? 'bill' : 'bills'} (and any attached files). This cannot be undone.`}
        confirmText="Delete"
        isDanger
        isLoading={deletingUtility}
      />

      <ConfirmationModal
        isOpen={!!confirmDeleteBill}
        onClose={() => setConfirmDeleteBill(null)}
        onConfirm={handleDeleteBill}
        title="Delete Bill?"
        message="This will permanently delete the bill record and any attached file. This cannot be undone."
        confirmText="Delete"
        isDanger
        isLoading={deletingBill}
      />
    </div>
  );
};

export default UtilityDetailsPage;
